import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { type ReactNode,useEffect, useId } from 'react'
import { createPortal } from 'react-dom'

import { mergeReducedMotionPreset, useThemeMotion } from '../../../animation/themeMotion'
import { ThemeModalCloseButton } from '../../modal/ThemeModalCloseButton'
import { comicPanelRound } from '../comicPanelTokens'

type Props = {
  open: boolean
  angular: boolean
  title: string
  subtitle?: string
  onClose: () => void
  children: ReactNode
}

/**
 * 漫画阅读器全屏层：与 ThemeModal / ImageLightbox 共用 theme-modal-backdrop 与面板变量。
 */
export function ComicReaderOverlay({ open, angular, title, subtitle, onClose, children }: Props) {
  const titleId = useId()
  const prefersReduced = useReducedMotion()
  const motionPreset = useThemeMotion()
  const preset = mergeReducedMotionPreset(motionPreset, !!prefersReduced)
  const overlayDur = Math.min(0.32, Math.max(0.2, preset.duration * 0.55))
  const panelDur = Math.min(0.38, Math.max(0.22, preset.expandDuration * 0.65))
  const ease = [...preset.easing] as [number, number, number, number]

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          key="comic-reader"
          className="fixed inset-0 z-[185] flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: overlayDur, ease }}
        >
          <button
            type="button"
            className="theme-modal-backdrop absolute inset-0 cursor-default border-0"
            aria-label="关闭阅读器"
            onClick={onClose}
          />
          <motion.div
            className={[
              'relative z-[1] mx-auto flex h-dvh w-full max-w-5xl flex-col border border-line bg-surface shadow-2xl',
              comicPanelRound(angular),
              angular ? 'sm:my-2 sm:h-[calc(100dvh-1rem)]' : 'sm:my-3 sm:h-[calc(100dvh-1.5rem)]',
            ].join(' ')}
            initial={{ opacity: 0, y: prefersReduced ? 0 : 16, scale: prefersReduced ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: prefersReduced ? 0 : 12, scale: prefersReduced ? 1 : 0.98 }}
            transition={{ duration: panelDur, ease }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-3 py-3 sm:px-5">
              <div className="min-w-0">
                <h2 id={titleId} className="truncate text-base font-semibold text-ink sm:text-lg">
                  {title}
                </h2>
                {subtitle ? <p className="mt-0.5 text-xs text-ink-soft">{subtitle}</p> : null}
              </div>
              <ThemeModalCloseButton angular={angular} onClick={onClose} />
            </header>
            <div className="flex min-h-0 flex-1 flex-col">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
